import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";

// FUNCTIONS
import { AuthProvider } from "./context/AuthContext";
import typography from "./components/typography";
import { ProductProvider } from "./context/productService";
import { CartProvider } from "./context/CartContext";
import { SearchProvider } from "./context/SearchContext";
import { WishlistProvider } from "./context/WishlistContext";
import PrivateRoute from "./pages/Auth/PrivateRoute";

import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppLoading from "./components/Loadings/AppLoading";
import PrivateAdminRoute from "./pages/Auth/PrivateAdminRoute";
import { AdminProvider } from "./context/AdminContext";
import ScrollToTop from "./components/ScrollToTop";
import { SellerAuthProvider } from "./context/SellerAuthContext";

import EmailInputStep from "./pages/Sellers/EmailInputStep";
import PasswordInputStep from "./pages/Sellers/PasswordInputStep";
import PhoneInputStep from "./pages/Sellers/PhoneInputStep";
import SellerDetailsStep from "./pages/Sellers/SellerDetailsStep";
import CookieConsent from "./components/CookieConsent";

typography.injectStyles();

//PAGES & COMPONENTS
const HomePage = lazy(() => import("./pages/HomePage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentMethods = lazy(() => import("./pages/PaymentMethods"));
const OrderProcessing = lazy(() => import("./pages/OrderProcessing"));

const AdminDashboard = lazy(() => import("./pages/Admin/AdminDashboard"));
const AdminProductsPage = lazy(() => import("./pages/Admin/AdminProductsPage"));
const AdminUsersPage = lazy(() => import("./pages/Admin/AdminUsersPage"));
const AdminOrdersPage = lazy(() => import("./pages/Admin/AdminOrdersPage"));
const AdminPaymentsPage = lazy(() => import("./pages/Admin/AdminPaymentsPage"));
const AdminReportsPage = lazy(() => import("./pages/Admin/AdminReportsPage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Wishlist = lazy(() => import("./pages/Settings/Wishlist"));
const Reports = lazy(() => import("./pages/Settings/Reports"));
const Orders = lazy(() => import("./pages/Settings/Orders"));
const Help = lazy(() => import("./pages/Settings/HelpCenter"));
const SettingContent = lazy(() => import("./pages/Settings/SettingContent"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const Profile = lazy(() => import("./pages/Settings/Profile"));
const AddressBook = lazy(() => import("./pages/Settings/AddressBook"));
const Messages = lazy(() => import("./pages/Settings/Messages"));
const SearchPage = lazy(() => import("./components/SearchPage"));
const AdminAds = lazy(() => import("./pages/Admin/AdminAds"));
const Admin = lazy(() => import("./pages/Admin/Admin"));
const AdminCategory = lazy(() => import("./pages/Admin/AdminCategory"));
const DIscountPage = lazy(() => import("./pages/DIscountPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerificationPage = lazy(() => import("./pages/VerificationPage"));
const Shipped = lazy(() => import("./pages/Settings/Shipped"));
const PrivacyPolicy = lazy(() => import("./pages/Settings/PrivacyPolicy"));
const ReturnPolicy = lazy(() => import("./pages/Settings/ReturnPolicy"));
const TermsAndCo = lazy(() => import("./pages/Settings/TermsAndCo"));
const AboutUs = lazy(() => import("./pages/Settings/AboutUs"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const AddProducts = lazy(() => import("./pages/Admin/AddProducts"));
const ManageProducts = lazy(() => import("./pages/Admin/ManageProducts"));
const ManageOrders = lazy(() => import("./pages/Admin/ManageOrders"));
const DepartmentPage = lazy(() => import("./pages/DepartmentPage"));
const Tracking = lazy(() => import("./pages/Settings/Tracking"));
const OrderTracking = lazy(() => import("./pages/Settings/OrderTracking"));
const OrderConfirmationPage = lazy(
  () => import("./pages/OrderConfirmationPage"),
);
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const OnlineOrderConfirmationPage = lazy(
  () => import("./pages/OnlineOrderConfirmationPage"),
);
// const OrderDetailsPage = lazy(() => import("./pages/OrderDetailsPage"));

const App = () => {
  return (
    <AuthProvider>
      <SellerAuthProvider>
        <SearchProvider>
          <ProductProvider>
            <WishlistProvider>
              <CartProvider>
                <AdminProvider>
                  <Router>
                    <ScrollToTop />
                    <ToastContainer
                      stacked={true}
                      position="top-right"
                      autoClose={1000}
                      hideProgressBar={false}
                      newestOnTop={true}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="colored"
                      transition={Bounce}
                    />
                    <Header />
                    <main className=" min-h-screen min-w-full pt-20 mt-4 text-small font-robo bg-gray-200 overflow-hidden">
                      <CookieConsent />
                      <Suspense fallback={<AppLoading />}>
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/register" element={<Register />} />

                          <Route path="/Login" element={<Login />} />
                          <Route path="/forgotPassword" element={<ForgotPassword />} />
                          <Route
                            path="/register/email"
                            element={<EmailInputStep />}
                          />
                          <Route
                            path="/register/password"
                            element={<PasswordInputStep />}
                          />
                          <Route
                            path="/register/phone"
                            element={<PhoneInputStep />}
                          />
                          <Route
                            path="/register/mobile-money"
                            element={<SellerDetailsStep />}
                          />
                          <Route
                            path="/updatePassword"
                            element={
                              <PrivateRoute>
                                <UpdatePassword />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/deleteAccount"
                            element={
                              <PrivateRoute>
                                <DeleteAccountPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/verify-email"
                            element={<VerificationPage />}
                          />
                          <Route path="/search" element={<SearchPage />} />
                          <Route
                            path="/product/:productId"
                            element={<ProductDetailPage />}
                          />
                          <Route
                            path="/categories/:categoryId"
                            element={<CategoriesPage />}
                          />
                          <Route
                            path="/department/:departmentId"
                            element={<DepartmentPage />}
                          />
                          <Route
                            path="/discount/:discountId"
                            element={<DIscountPage />}
                          />
                          <Route path="/cart" element={<CartPage />} />
                          <Route
                            path="/checkout"
                            element={
                              <PrivateRoute>
                                <CheckoutPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/order-confirmation"
                            element={
                              <PrivateRoute>
                                <OrderConfirmationPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/orders"
                            element={
                              <PrivateRoute>
                                <OrdersPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/online-order-confirmation"
                            element={
                              <PrivateRoute>
                                <OnlineOrderConfirmationPage />
                              </PrivateRoute>
                            }
                          />

                          {/* <Route path="/order-details/:orderId" element={<PrivateRoute><OrderDetailsPage /></PrivateRoute>} /> */}
                          <Route
                            path="/process"
                            element={
                              <PrivateRoute>
                                <OrderProcessing />
                              </PrivateRoute>
                            }
                          />
                          <Route path="/settings" element={<Settings />}>
                            <Route
                              path="setting"
                              element={
                                <PrivateRoute>
                                  <SettingContent />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="wishlist"
                              element={
                                <PrivateRoute>
                                  <Wishlist />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="report"
                              element={
                                <PrivateRoute>
                                  <Reports />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="orders"
                              element={
                                <PrivateRoute>
                                  <Orders />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="track"
                              element={
                                <PrivateRoute>
                                  <Tracking />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="orderTrack/:orderId"
                              element={
                                <PrivateRoute>
                                  <OrderTracking />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="shipped"
                              element={
                                <PrivateRoute>
                                  <Shipped />
                                </PrivateRoute>
                              }
                            />
                            <Route path="help" element={<Help />} />
                            <Route path="terms" element={<TermsAndCo />} />
                            <Route path="about" element={<AboutUs />} />
                            <Route
                              path="returnPolicy"
                              element={<ReturnPolicy />}
                            />
                            <Route
                              path="privacyPolicy"
                              element={<PrivacyPolicy />}
                            />
                            <Route
                              path="profile"
                              element={
                                <PrivateRoute>
                                  <Profile />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="address_book"
                              element={
                                <PrivateRoute>
                                  <AddressBook />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="messages"
                              element={
                                <PrivateRoute>
                                  <Messages />
                                </PrivateRoute>
                              }
                            />
                          </Route>
                          <Route
                            path="/payMethods"
                            element={
                              <PrivateRoute>
                                <PaymentMethods />
                              </PrivateRoute>
                            }
                          ></Route>
                          <Route
                            path="/admin"
                            element={
                              <PrivateAdminRoute>
                                <AdminDashboard />
                              </PrivateAdminRoute>
                            }
                          >
                            <Route path="dashboard" element={<Admin />} />
                            <Route
                              path="products"
                              element={<AdminProductsPage />}
                            />
                            <Route path="users" element={<AdminUsersPage />} />
                            <Route
                              path="addProduct"
                              element={<AddProducts />}
                            />
                            <Route
                              path="manageProduct"
                              element={<ManageProducts />}
                            />
                            <Route
                              path="categories"
                              element={<AdminCategory />}
                            />
                            <Route
                              path="orders"
                              element={<AdminOrdersPage />}
                            />
                            <Route
                              path="manageOrder"
                              element={<ManageOrders />}
                            />
                            <Route path="ads" element={<AdminAds />} />

                            <Route
                              path="payments"
                              element={<AdminPaymentsPage />}
                            />

                            <Route
                              path="reports"
                              element={<AdminReportsPage />}
                            />
                          </Route>
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </main>
                    <Footer />
                  </Router>
                </AdminProvider>
              </CartProvider>
            </WishlistProvider>
          </ProductProvider>
        </SearchProvider>
      </SellerAuthProvider>
    </AuthProvider>
  );
};

export default App;
